import { cleanSpeech } from './callTurns';

/** One transcript fragment on the session timeline. */
export interface TranscriptFragment {
  delta: string;
  start_ms: number;
  end_ms: number;
}

/**
 * The caller's words that no one has answered yet.
 *
 * GPT-Live's delegation event says *when* it decided to delegate but not
 * *what* was asked, so the request is rebuilt from the input transcript.
 *
 * - When the assistant starts speaking, what the caller had finished saying
 *   *may* have been answered ("hello" → "hi Eric"). That is only provisional:
 *   GPT-Live often says "sure, one sec" before it delegates, so the boundary
 *   is just marked. It is committed (those words become a voice-only turn)
 *   when the caller speaks again, and cancelled if a delegation follows, so
 *   the words stay in the request.
 * - Input transcript can arrive after the assistant's reply to it; a late
 *   fragment that ends before the boundary still belongs to the answered turn.
 * - A delegation can fire before the caller finishes the sentence, so the
 *   request is taken only once the transcript has gone quiet, not cut at the
 *   delegation's offset (which split "…the workflows | page").
 * - Turns follow the timeline, not arrival: a fragment that starts within
 *   {@link CONTINUE_MS} of the answered words' end is the same breath, still
 *   part of the answered turn, even if the assistant started meanwhile.
 */
/** A pause shorter than this keeps the caller's words in one turn. */
const CONTINUE_MS = 500;

const join = (fragments: readonly TranscriptFragment[]): string =>
  cleanSpeech(fragments.map((f) => f.delta).join(''));

export class TranscriptBuffer {
  private fragments: TranscriptFragment[] = [];
  private claims = 0;
  /** Where the assistant started answering, on the session timeline. */
  private answeredUntil: number | null = null;
  /** Wall-clock time of the last input fragment, for quiet detection. */
  lastInputAt = 0;

  push(fragment: TranscriptFragment, now = Date.now()): void {
    this.fragments.push(fragment);
    this.lastInputAt = now;
  }

  /** A fragment of a turn the assistant has already started answering. */
  isLate(fragment: TranscriptFragment): boolean {
    if (this.answeredUntil === null) return false;
    if (fragment.end_ms <= this.answeredUntil) return true;
    const runEnd = this.answeredRunEnd();
    return runEnd !== null && fragment.start_ms - runEnd < CONTINUE_MS;
  }

  /**
   * Where the answered words end on the timeline: the words before the
   * boundary, and every fragment that carried on from them without a pause.
   */
  private answeredRunEnd(): number | null {
    if (this.answeredUntil === null) return null;
    let end: number | null = null;
    const byStart = [...this.fragments].sort((a, b) => a.start_ms - b.start_ms);
    for (const fragment of byStart) {
      const before = fragment.end_ms <= this.answeredUntil;
      const carriesOn = end !== null && fragment.start_ms - end < CONTINUE_MS;
      if (before || carriesOn) end = Math.max(end ?? 0, fragment.end_ms);
    }
    return end;
  }

  /**
   * The assistant started speaking at `startMs`: mark, don't commit.
   * @returns whether this set the boundary.
   */
  noteAssistantSpeech(startMs: number): boolean {
    if (this.claims > 0 || this.answeredUntil !== null) return false;
    this.answeredUntil = startMs;
    return true;
  }

  /** The words the assistant may have answered (shown, not yet saved). */
  answeredText(): string {
    return join(this.fragments.filter((f) => this.isLate(f)));
  }

  /** Words after the boundary: the caller's current turn. */
  pendingText(): string {
    return join(this.fragments.filter((f) => !this.isLate(f)));
  }

  /** The answered turn is settled: take its words. */
  commitAnswered(): string {
    const answered = this.fragments.filter((f) => this.isLate(f));
    this.fragments = this.fragments.filter((f) => !this.isLate(f));
    this.answeredUntil = null;
    return join(answered);
  }

  /** The caller's current words were only a backchannel ("mhm"): forget them. */
  dropPending(): void {
    this.fragments = this.fragments.filter((f) => this.isLate(f));
  }

  /** It was not an answer after all (a delegation followed): keep the words. */
  cancelAnswered(): void {
    this.answeredUntil = null;
  }

  /** Everything the caller has said that is not yet a message. */
  text(): string {
    return join(this.fragments);
  }

  /** Hold the buffer for a delegation; release with {@link take}. */
  claim(): void {
    this.claims += 1;
  }

  /** Remove and join every pending fragment, releasing one claim. */
  take(): string {
    this.claims = Math.max(0, this.claims - 1);
    const text = join(this.fragments);
    this.fragments = [];
    this.answeredUntil = null;
    return text;
  }
}

export interface CollectOptions {
  /** The caller has finished once input has been quiet this long. */
  quietMs: number;
  /** Never hold a delegation longer than this. */
  maxWaitMs: number;
  pollMs: number;
}

/**
 * Claim the buffer for a delegation, wait for the caller to finish speaking,
 * and take what they said.
 */
export async function collectRequest(
  buffer: TranscriptBuffer,
  { quietMs, maxWaitMs, pollMs }: CollectOptions,
): Promise<string> {
  buffer.claim();
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() - buffer.lastInputAt < quietMs && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }
  return buffer.take();
}

/**
 * GPT-Live caps one commentary append at 500 tokens. ~4 characters a token,
 * with headroom; anything longer is cut at a sentence boundary when possible.
 */
const COMMENTARY_MAX_CHARS = 1_600;

export function toCommentary(reply: string): string {
  const text = reply
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[*_#>`|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= COMMENTARY_MAX_CHARS) return text;
  const cut = text.slice(0, COMMENTARY_MAX_CHARS);
  const sentenceEnd = cut.lastIndexOf('. ');
  return (
    (sentenceEnd > COMMENTARY_MAX_CHARS / 2
      ? cut.slice(0, sentenceEnd + 1)
      : cut) + ' The full answer is in the chat panel.'
  );
}
